import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { affiliateCopy, type AffiliateCopy } from "../i18n/affiliates";

type AffiliateProps = { localeCode: string };
type Balances = { pending_cents: number; available_cents: number; reserved_cents: number; paid_cents: number; adjustment_cents: number };
type Account = { user_id: string; code: string; beneficiary: string | null; iban: string | null; bic: string | null; activated_at: string };
type Summary = { account: Account | null; link: string | null; network: { direct: number; indirect: number }; balances: Balances };
type Commission = { id: string; level: number; amount_cents: number; current_amount_cents: number; paid_cents: number; reserved_cents: number; blocked: number | boolean; available_at: string; created_at: string; currency: string; rate_bps: number; net_amount_cents: number };
type Payout = { id: string; user_id: string; period: string; amount_cents: number; status: "prepared" | "paid" | "cancelled"; beneficiary: string; iban: string; bic: string; reference: string | null; paid_at: string | null; created_at: string };
type OpsAccount = Account & Balances & { display_name: string; direct_count: number; indirect_count: number };
type List<T> = { data: T[]; hasMore: boolean };
type Notice = { error: boolean; text: string } | null;

class AffiliateRequestError extends Error {
  constructor(readonly code: string) { super(code); }
}

async function requestApi<T>(path: string, method = "GET", body?: unknown, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: { accept: "application/json", ...(body === undefined ? {} : { "content-type": "application/json" }) },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal
  });
  const payload = await response.json();
  if (!response.ok) throw new AffiliateRequestError(payload !== null && typeof payload === "object" && "code" in payload && typeof payload.code === "string" ? payload.code : "affiliate_invalid_request");
  return payload as T;
}

function errorText(error: unknown, copy: AffiliateCopy): string {
  if (!(error instanceof AffiliateRequestError)) return copy.error;
  const keys: Record<string, keyof AffiliateCopy> = {
    unauthorized: "unauthorized", forbidden: "forbidden", affiliate_not_active: "notActivated",
    affiliate_invalid_bank_details: "invalidBank", affiliate_invalid_period: "invalidPeriod",
    affiliate_no_payable_balance: "noBalance", affiliate_payout_conflict: "payoutConflict",
    affiliate_payout_not_found: "notFound", affiliate_invalid_payment_reference: "invalidTransfer",
    affiliate_invalid_payment_date: "invalidTransfer", account_inactive: "inactive"
  };
  return copy[keys[error.code] ?? "error"];
}

function money(cents: number, localeCode: string): string {
  return new Intl.NumberFormat(localeCode, { style: "currency", currency: "EUR" }).format(cents / 100);
}

function dateText(value: string | null, localeCode: string): string {
  return value === null ? "" : new Intl.DateTimeFormat(localeCode, { dateStyle: "medium" }).format(new Date(value));
}

function monthText(value: string, localeCode: string): string {
  return new Intl.DateTimeFormat(localeCode, { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${value}-01T00:00:00Z`));
}

function NoticeView({ notice }: { notice: Notice }) {
  return notice && <p className={`affiliate-notice${notice.error ? " affiliate-notice-error" : ""}`} role={notice.error ? "alert" : "status"}>{notice.text}</p>;
}

function Pager({ page, hasMore, busy, copy, onChange }: { page: number; hasMore: boolean; busy: boolean; copy: AffiliateCopy; onChange: (page: number) => void }) {
  if (page === 1 && !hasMore) return null;
  return <nav className="affiliate-pagination" aria-label={copy.page}>
    <button className="base-button sm" type="button" disabled={page === 1 || busy} onClick={() => onChange(page - 1)}>{copy.previous}</button>
    <span>{copy.page} {page}</span>
    <button className="base-button sm" type="button" disabled={!hasMore || busy} onClick={() => onChange(page + 1)}>{copy.next}</button>
  </nav>;
}

function Table({ headers, empty, children, hasRows }: { headers: string[]; empty: string; children: ReactNode; hasRows: boolean }) {
  return hasRows ? <div className="affiliate-table-wrap"><table className="affiliate-table"><thead><tr>{headers.map((header) => <th scope="col" key={header}>{header}</th>)}</tr></thead><tbody>{children}</tbody></table></div> : <p className="affiliate-empty">{empty}</p>;
}

function BalanceCards({ balances, localeCode, copy }: { balances: Balances; localeCode: string; copy: AffiliateCopy }) {
  const values: Array<[keyof Balances, string]> = [["pending_cents", copy.pending], ["available_cents", copy.available], ["reserved_cents", copy.reserved], ["paid_cents", copy.paid]];
  return <><dl className="affiliate-metrics">{values.map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{money(balances[key], localeCode)}</dd></div>)}</dl>{balances.adjustment_cents < 0 && <p className="affiliate-notice">{copy.adjustment}: {money(balances.adjustment_cents, localeCode)}</p>}</>;
}

function commissionStatus(row: Commission, copy: AffiliateCopy): string {
  if (row.blocked) return copy.blocked;
  if (row.current_amount_cents <= 0) return copy.reversed;
  if (row.paid_cents >= row.current_amount_cents) return copy.paid;
  if (row.reserved_cents > 0) return copy.reserved;
  return Date.parse(row.available_at) > Date.now() ? copy.pending : copy.available;
}

export function AffiliateView({ localeCode }: AffiliateProps) {
  const copy = affiliateCopy(localeCode);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [commissions, setCommissions] = useState<List<Commission>>({ data: [], hasMore: false });
  const [payouts, setPayouts] = useState<List<Payout>>({ data: [], hasMore: false });
  const [commissionPage, setCommissionPage] = useState(1);
  const [payoutPage, setPayoutPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [bank, setBank] = useState({ beneficiary: "", iban: "", bic: "" });
  const reload = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    void (async () => {
      try {
        const next = await requestApi<Summary>("/api/affiliates", "GET", undefined, controller.signal);
        if (next.account) {
          const [nextCommissions, nextPayouts] = await Promise.all([
            requestApi<List<Commission>>(`/api/affiliates/commissions?page=${commissionPage}`, "GET", undefined, controller.signal),
            requestApi<List<Payout>>(`/api/affiliates/payouts?page=${payoutPage}`, "GET", undefined, controller.signal)
          ]);
          if (controller.signal.aborted) return;
          setCommissions(nextCommissions);
          setPayouts(nextPayouts);
          setBank({ beneficiary: next.account.beneficiary ?? "", iban: next.account.iban ?? "", bic: next.account.bic ?? "" });
        }
        if (!controller.signal.aborted) setSummary(next);
      } catch (error) {
        if (!controller.signal.aborted) setNotice({ error: true, text: errorText(error, copy) });
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [commissionPage, payoutPage, revision, copy]);

  async function action(path: string, method: string, body: unknown, success: string) {
    setBusy(true);
    setNotice(null);
    try {
      await requestApi(path, method, body);
      setNotice({ error: false, text: success });
      reload();
    } catch (error) {
      setNotice({ error: true, text: errorText(error, copy) });
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!summary?.link) return;
    try {
      await navigator.clipboard.writeText(summary.link);
      setNotice({ error: false, text: copy.copied });
    } catch {
      setNotice({ error: true, text: copy.clipboardError });
    }
  }

  function saveBank(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void action("/api/affiliates/payout-details", "PATCH", bank, copy.saved);
  }

  return <section className="contract-page affiliate-page" aria-busy={loading || busy}>
    <header className="affiliate-heading"><div><h1>{copy.title}</h1><p>{copy.lead}</p></div><button className="base-button sm" type="button" disabled={loading || busy} onClick={reload}>{copy.refresh}</button></header>
    <NoticeView notice={notice} />
    <section className="affiliate-panel"><p>{copy.rules}</p><p>{copy.attribution}</p><p>{copy.adjustmentNote}</p></section>
    {loading && <p role="status">{copy.loading}</p>}
    {summary && !summary.account && <section className="affiliate-panel"><p>{copy.activationNote}</p><button className="base-button primary" type="button" disabled={busy || loading} onClick={() => void action("/api/affiliates/activate", "POST", {}, copy.activated)}>{busy ? copy.saving : copy.activate}</button></section>}
    {summary?.account && <>
      <section className="affiliate-panel settings-form"><label>{copy.referralLink}<input readOnly value={summary.link ?? ""} onFocus={(event) => event.currentTarget.select()} /></label><div className="affiliate-actions"><button className="base-button primary" type="button" onClick={() => void copyLink()}>{copy.copy}</button></div></section>
      <dl className="affiliate-metrics affiliate-network"><div><dt>{copy.direct}</dt><dd>{new Intl.NumberFormat(localeCode).format(summary.network.direct)}</dd></div><div><dt>{copy.indirect}</dt><dd>{new Intl.NumberFormat(localeCode).format(summary.network.indirect)}</dd></div></dl>
      <BalanceCards balances={summary.balances} localeCode={localeCode} copy={copy} />
      <form className="settings-form affiliate-bank-form" onSubmit={saveBank}><h2>{copy.bankTitle}</h2><p>{copy.bankNote}</p><fieldset disabled={busy || loading} className="entity-form-fields"><label>{copy.beneficiary}<input autoComplete="name" required name="beneficiary" maxLength={160} value={bank.beneficiary} onChange={(event) => setBank({ ...bank, beneficiary: event.currentTarget.value })} /></label><div className="form-two"><label>{copy.iban}<input required name="iban" autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={42} value={bank.iban} onChange={(event) => setBank({ ...bank, iban: event.currentTarget.value })} /></label><label>{copy.bic}<input required name="bic" autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={11} value={bank.bic} onChange={(event) => setBank({ ...bank, bic: event.currentTarget.value })} /></label></div><div className="affiliate-actions"><button className="base-button primary" type="submit">{busy ? copy.saving : copy.save}</button></div></fieldset></form>
      <section className="affiliate-panel"><h2>{copy.commissions}</h2><Table headers={[copy.date, copy.level, copy.rate, copy.base, copy.earned, copy.maturity, copy.status]} empty={copy.empty} hasRows={commissions.data.length > 0}>{commissions.data.map((row) => <tr key={row.id}><td>{dateText(row.created_at, localeCode)}</td><td>{row.level}</td><td>{new Intl.NumberFormat(localeCode).format(row.rate_bps / 100)}%</td><td>{money(row.net_amount_cents, localeCode)}</td><td>{money(row.current_amount_cents, localeCode)}</td><td>{dateText(row.available_at, localeCode)}</td><td>{commissionStatus(row, copy)}</td></tr>)}</Table><Pager page={commissionPage} hasMore={commissions.hasMore} busy={busy || loading} copy={copy} onChange={setCommissionPage} /></section>
      <section className="affiliate-panel"><h2>{copy.payouts}</h2><Table headers={[copy.period, copy.amount, copy.status, copy.reference, copy.paidAt]} empty={copy.empty} hasRows={payouts.data.length > 0}>{payouts.data.map((row) => <tr key={row.id}><td>{monthText(row.period, localeCode)}</td><td>{money(row.amount_cents, localeCode)}</td><td>{copy[row.status]}</td><td>{row.reference}</td><td>{dateText(row.paid_at, localeCode)}</td></tr>)}</Table><Pager page={payoutPage} hasMore={payouts.hasMore} busy={busy || loading} copy={copy} onChange={setPayoutPage} /></section>
    </>}
  </section>;
}

function PayoutActions({ payout, copy, busy, action }: { payout: Payout; copy: AffiliateCopy; busy: boolean; action: (id: string, kind: "paid" | "cancel", body: unknown) => Promise<void> }) {
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState("");
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void action(payout.id, "paid", { reference, paidAt });
  }
  return <form className="affiliate-transfer-form settings-form" onSubmit={submit}><fieldset className="entity-form-fields" disabled={busy}><div className="form-two"><label>{copy.reference}<input required maxLength={160} value={reference} onChange={(event) => setReference(event.currentTarget.value)} /></label><label>{copy.paidAt}<input required type="date" max={new Date().toISOString().slice(0, 10)} value={paidAt} onChange={(event) => setPaidAt(event.currentTarget.value)} /></label></div><div className="affiliate-actions"><button className="base-button primary" type="submit">{copy.markPaid}</button><button className="base-button" type="button" onClick={() => void action(payout.id, "cancel", {})}>{copy.cancel}</button></div></fieldset></form>;
}

export function AffiliateOpsView({ localeCode }: AffiliateProps) {
  const copy = affiliateCopy(localeCode);
  const [accounts, setAccounts] = useState<List<OpsAccount>>({ data: [], hasMore: false });
  const [payouts, setPayouts] = useState<List<Payout>>({ data: [], hasMore: false });
  const [accountPage, setAccountPage] = useState(1);
  const [payoutPage, setPayoutPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [userId, setUserId] = useState("");
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const reload = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    void Promise.all([
      requestApi<List<OpsAccount>>(`/api/ops/affiliates?page=${accountPage}`, "GET", undefined, controller.signal),
      requestApi<List<Payout>>(`/api/ops/affiliates/payouts?page=${payoutPage}`, "GET", undefined, controller.signal)
    ]).then(([nextAccounts, nextPayouts]) => {
      if (!controller.signal.aborted) { setAccounts(nextAccounts); setPayouts(nextPayouts); }
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) setNotice({ error: true, text: errorText(error, copy) });
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [accountPage, payoutPage, revision, copy]);

  async function mutate(path: string, body: unknown, success: string) {
    setBusy(true);
    setNotice(null);
    try {
      await requestApi(path, "POST", body);
      setNotice({ error: false, text: success });
      reload();
    } catch (error) {
      setNotice({ error: true, text: errorText(error, copy) });
    } finally { setBusy(false); }
  }

  function prepare(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void mutate("/api/ops/affiliates/payouts", { userId, period }, copy.preparedNotice);
  }

  async function payoutAction(id: string, kind: "paid" | "cancel", body: unknown) {
    await mutate(`/api/ops/affiliates/payouts/${encodeURIComponent(id)}/${kind}`, body, kind === "paid" ? copy.paidNotice : copy.cancelledNotice);
  }

  return <section className="contract-page affiliate-page" aria-busy={busy || loading}>
    <header className="affiliate-heading"><div><h1>{copy.opsTitle}</h1><p>{copy.opsLead}</p></div><button className="base-button sm" type="button" disabled={busy || loading} onClick={reload}>{copy.refresh}</button></header>
    <NoticeView notice={notice} />
    {loading && <p role="status">{copy.loading}</p>}
    <section className="affiliate-panel"><h2>{copy.title}</h2><Table headers={[copy.member, copy.code, copy.direct, copy.indirect, copy.pending, copy.available, copy.reserved, copy.paid, copy.adjustment]} empty={copy.empty} hasRows={accounts.data.length > 0}>{accounts.data.map((row) => <tr key={row.user_id}><th scope="row">{row.display_name}</th><td>{row.code}</td><td>{row.direct_count}</td><td>{row.indirect_count}</td><td>{money(row.pending_cents, localeCode)}</td><td>{money(row.available_cents, localeCode)}</td><td>{money(row.reserved_cents, localeCode)}</td><td>{money(row.paid_cents, localeCode)}</td><td>{money(row.adjustment_cents, localeCode)}</td></tr>)}</Table><Pager page={accountPage} hasMore={accounts.hasMore} busy={busy || loading} copy={copy} onChange={setAccountPage} /></section>
    <form className="settings-form" onSubmit={prepare}><h2>{copy.prepareTitle}</h2><p>{copy.prepareNote}</p><fieldset className="entity-form-fields" disabled={busy || loading}><div className="form-two"><label>{copy.member}<select required value={userId} onChange={(event) => setUserId(event.currentTarget.value)}><option value="">{copy.selectMember}</option>{accounts.data.map((row) => <option value={row.user_id} key={row.user_id}>{row.display_name} ({row.code})</option>)}</select></label><label>{copy.period}<input required type="month" value={period} onChange={(event) => setPeriod(event.currentTarget.value)} /></label></div><div className="affiliate-actions"><button className="base-button primary" type="submit">{busy ? copy.saving : copy.prepare}</button></div></fieldset></form>
    <section className="affiliate-panel"><h2>{copy.payouts}</h2>{payouts.data.length === 0 && <p className="affiliate-empty">{copy.empty}</p>}<div className="affiliate-payout-list">{payouts.data.map((row) => <article className="affiliate-payout" key={row.id}><header><div><h3>{row.beneficiary}</h3><p>{monthText(row.period, localeCode)} · {copy[row.status]}</p></div><strong>{money(row.amount_cents, localeCode)}</strong></header><dl className="affiliate-bank-snapshot"><div><dt>{copy.iban}</dt><dd>{row.iban}</dd></div><div><dt>{copy.bic}</dt><dd>{row.bic}</dd></div>{row.reference && <div><dt>{copy.reference}</dt><dd>{row.reference}</dd></div>}{row.paid_at && <div><dt>{copy.paidAt}</dt><dd>{dateText(row.paid_at, localeCode)}</dd></div>}</dl>{row.status === "prepared" && <PayoutActions payout={row} copy={copy} busy={busy || loading} action={payoutAction} />}</article>)}</div><Pager page={payoutPage} hasMore={payouts.hasMore} busy={busy || loading} copy={copy} onChange={setPayoutPage} /></section>
  </section>;
}
